/**
 * Shapes mirrored from the backend `account-deletion` module.
 */
export type DeletionStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';
export type DeletionSubjectType = 'USER' | 'VENDOR';
export type DeletionSource = 'IN_APP' | 'PUBLIC_FORM';

export const DELETION_STATUSES = ['PENDING', 'APPROVED', 'REJECTED', 'CANCELLED'] as const;
export const DELETION_SUBJECT_TYPES = ['USER', 'VENDOR'] as const;
export const DELETION_SOURCES = ['IN_APP', 'PUBLIC_FORM'] as const;

export type OpenCommitments = {
  active_bookings: number;
  active_orders: number;
  /** Captured minus refunded, as a decimal string. */
  net_captured_inr: string;
};

export type AccountDeletionRequest = {
  id: string;
  source: DeletionSource;
  subject_type: DeletionSubjectType;
  status: DeletionStatus;
  reason: string | null;
  decision_note: string | null;
  decided_at: string | null;
  cancelled_at: string | null;
  /** When the data may be erased. Set on approval. */
  purge_after: string | null;
  purged_at: string | null;
  created_at: string;
  /** Null when a public-form request matched no account — the ones needing identification. */
  user_id: string | null;
  vendor_profile_id: string | null;
  contact_name: string | null;
  contact_phone: string | null;
  contact_email: string | null;
  business_name: string | null;
  /** False once the closure has been applied; null when no account was ever matched. */
  account_is_active: boolean | null;
  decided_by_admin_id: string | null;
  decided_by_admin_email: string | null;
  updated_at: string;
};

export type AccountDeletionDetail = AccountDeletionRequest & {
  open_commitments: OpenCommitments;
};

export type AccountDeletionListResponse = {
  items: AccountDeletionRequest[];
  total: number;
  page: number;
  page_size: number;
};

export type AccountDeletionFilters = {
  page?: number;
  page_size?: number;
  status?: DeletionStatus | '';
  subject_type?: DeletionSubjectType | '';
  source?: DeletionSource | '';
  /** Tri-state, as the URL carries it: '' / 'true' / 'false'. */
  matched?: string;
  q?: string;
  from?: string;
  to?: string;
};

export type AccountDeletionCounts = {
  pending: number;
  pending_users: number;
  pending_vendors: number;
  /** Public-form requests still waiting — identity unproven. */
  pending_public: number;
  /** Approved, inside the grace window, not yet erased. Still reversible. */
  in_grace_period: number;
  rejected: number;
  total: number;
  oldest_pending_hours: number | null;
};

export const ACCOUNT_DELETION_SORT_FIELDS = [
  'created_at',
  'decided_at',
  'purge_after',
  'status',
] as const;

/**
 * Whether this row is a job rather than a record.
 *
 * Only PENDING is. An approved request inside its window is watchable but not waiting on
 * anybody, and everything else is finished.
 */
export function needs_decision(r: AccountDeletionRequest): boolean {
  return r.status === 'PENDING';
}

/** Approved, not yet erased, window still open — the reversible state. */
export function in_grace_period(r: AccountDeletionRequest): boolean {
  return (
    r.status === 'APPROVED' &&
    r.purged_at === null &&
    r.purge_after !== null &&
    new Date(r.purge_after).getTime() > Date.now()
  );
}

/**
 * A public-form request nobody has tied to an account.
 *
 * The one row type that cannot be approved at all — you cannot close what you have not
 * identified — so it reads as its own state rather than as a pending request like any other.
 */
export function is_unidentified(r: AccountDeletionRequest): boolean {
  return r.user_id === null;
}

export function status_tone(
  r: AccountDeletionRequest,
): 'warning' | 'destructive' | 'neutral' | 'success' {
  if (r.status === 'PENDING') return 'warning';
  if (r.status === 'APPROVED') return 'destructive';
  if (r.status === 'CANCELLED') return 'success';
  return 'neutral';
}

/** Whole days left before the data is erased. Negative once the deadline has passed. */
export function days_until_purge(r: AccountDeletionRequest): number | null {
  if (!r.purge_after) return null;
  return Math.ceil((new Date(r.purge_after).getTime() - Date.now()) / 86_400_000);
}
